import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { PLAYER_TABLE_COLUMNS } from "../constants";

const VISIBLE_COLS_KEY = "rosterOS_visibleColumns";

function loadVisibleColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(VISIBLE_COLS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as string[];
      if (Array.isArray(parsed) && parsed.length > 0) return new Set(parsed);
    }
  } catch {}
  return new Set(PLAYER_TABLE_COLUMNS.map(c => c.id));
}

// ── Context shape ─────────────────────────────────────────────────

interface UIStateContextValue {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: (v: boolean) => void;
  visibleColumns: Set<string>;
  setVisibleColumns: Dispatch<SetStateAction<Set<string>>>;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
}

const UIStateContext = createContext<UIStateContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────

interface UIStateProviderProps {
  children: React.ReactNode;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
}

export function UIStateProvider({ children, darkMode, setDarkMode }: UIStateProviderProps) {
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [visibleColumnsState, setVisibleColumnsState] = useState<Set<string>>(loadVisibleColumns);

  const setVisibleColumns: Dispatch<SetStateAction<Set<string>>> = useCallback((next) => {
    setVisibleColumnsState(prev => {
      const resolved = typeof next === "function" ? next(prev) : next;
      try {
        localStorage.setItem(VISIBLE_COLS_KEY, JSON.stringify([...resolved]));
      } catch {}
      return resolved;
    });
  }, []);

  const contextValue = useMemo(() => ({
    mobileDrawerOpen, setMobileDrawerOpen, visibleColumns: visibleColumnsState, setVisibleColumns, darkMode, setDarkMode,
  }), [mobileDrawerOpen, setMobileDrawerOpen, visibleColumnsState, setVisibleColumns, darkMode, setDarkMode]);

  return (
    <UIStateContext.Provider value={contextValue}>
      {children}
    </UIStateContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────

export function useUIState(): UIStateContextValue {
  const ctx = useContext(UIStateContext);
  if (!ctx) throw new Error("useUIState must be used within UIStateProvider");
  return ctx;
}
